import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import AddStageModal from "./AddStageModal";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import RemoveStageMutation from "./mutation/RemoveStage";
import GetTeamsToNextStage from "./queries/GetTeamsToNextStage";
import TeamsToNextStageModal from "./TeamsToNextStageModal";
import { confirmDelete } from "../common/confirmDelete";
import PaginatedFlatTable from "../pagination/PaginatedFlatTable";
import useTranslation from "../../i18n/useTranslation"; // adjust path if needed
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function StageList({
                                      stages,
                                      setStages,
                                      activeSeasonData,
                                      division,
                                      waivers,
                                  }) {
    const { t } = useTranslation();
    const modalElem = useRef(null);

    const [removeStageMutation] = RemoveStageMutation();
    const [getTeamsToNextStage] = GetTeamsToNextStage();

    const [teamsToNextStage, setTeamsToNextStage] = useState([]);
    const [stageId, setStageId] = useState(null);

    // ✅ client-side pagination (since "stages" is already in memory)
    const PAGE_SIZE = 25;
    const [page, setPage] = useState(1);

    const totalCount = Array.isArray(stages) ? stages.length : 0;
    const pages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    useEffect(() => {
        if (page > pages) setPage(pages);
        if (page < 1) setPage(1);
    }, [page, pages]);

    const pageStart = useMemo(() => (page - 1) * PAGE_SIZE, [page]);
    const pageEnd = useMemo(() => pageStart + PAGE_SIZE, [pageStart]);

    const ui = useMemo(() => {
        return {
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                whiteSpace: "nowrap",
            },
            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                textDecoration: "none",
                lineHeight: 1,
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },
            actionBtnSuccess: {
                border: "1px solid rgba(40,167,69,0.35)",
                background: "rgba(40,167,69,0.06)",
                color: "#28a745",
            },
            muted: { color: "rgba(20,21,26,0.60)", fontWeight: 700, fontSize: 12.5 },
        };
    }, []);

    const removeStage = async (id) => {
        try {
            const res = await removeStageMutation({
                variables: { stageId: parseInt(id, 10) },
            });

            if (res.data?.deleteStage?.success) {
                setStages((prev) => prev.filter((s) => s.id !== id));

                toast.success(t("stageList.toasts.removeSuccess"), {
                    autoClose: 3000,
                    toastId: "remove_stage_success",
                    position: "top-center",
                    closeOnClick: true,
                });
            } else {
                toast.error(t("stageList.toasts.removeFailed"), {
                    autoClose: 3000,
                    toastId: "remove_stage_failed",
                    position: "top-center",
                    closeOnClick: true,
                });
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "stageList.toasts.removeFailed"), {
                autoClose: 3000,
                toastId: "remove_stage_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    const requestRemoveStage = (stage) => {
        confirmDelete({
            title: t("stageList.deleteDialog.title"),
            message: t("stageList.deleteDialog.message", { name: stage.name }),
            confirmLabel: t("stageList.deleteDialog.confirm"),
            cancelLabel: t("stageList.deleteDialog.cancel"),
            danger: true,
            onConfirm: async () => {
                await removeStage(stage.id);
            },
        });
    };

    const teamsNextStage = async (id) => {
        try {
            const res = await getTeamsToNextStage({ variables: { stageId: id } });
            if (res.data?.teamsToNextStage) setTeamsToNextStage(res.data.teamsToNextStage);
            setStageId(id);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                autoClose: 3000,
                toastId: "teams_next_stage_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    // ✅ Use PaginatedFlatTable's Add button, but keep Bootstrap modal trigger via a hidden proxy button
    const openAddStageModal = useCallback(() => {
        modalElem.current?.click();
    }, []);

    return (
        <>
            {/* Hidden proxy button keeps bootstrap attributes + ref for AddStageModal */}
            <button
                ref={modalElem}
                type="button"
                data-toggle="modal"
                data-target="#add-stage-modal"
                style={{ display: "none" }}
                aria-hidden="true"
                tabIndex={-1}
            />

            <div id="kbola-stage-list">
                <PaginatedFlatTable
                    title={t("stageList.title")}
                    subtitle={
                        <>
                            {t("stageList.subtitleManage")} <b>{activeSeasonData?.name || t("stageList.thisSeason")}</b> ·{" "}
                            <span style={{ color: "rgba(20,21,26,0.55)", fontWeight: 800 }}>
                                {t("stageList.total")}: {totalCount}
                            </span>
                        </>
                    }
                    page={page}
                    pages={pages}
                    items={stages || []}
                    loading={false}
                    onAdd={openAddStageModal}
                    onPageChange={(p) => setPage(p)}
                    headers={[
                        t("stageList.headers.stage"),
                        t("stageList.headers.type"),
                        t("stageList.headers.actions"),
                    ]}
                    renderRow={(stage, idx) => {
                        // ✅ client-side slicing without changing PaginatedFlatTable
                        if (idx < pageStart || idx >= pageEnd) return null;

                        const canDelete = stage.hasResults === false;

                        return (
                            <tr key={stage.slug || stage.id || idx}>
                                <td>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 900, color: "#14151a" }}>
                                            {stage.name}
                                        </div>
                                        <div style={ui.muted}>
                                            {stage.isDone
                                                ? t("stageList.status.completed")
                                                : t("stageList.status.inProgress")}
                                            {stage.nextStage ? ` · ${t("stageList.status.nextStageEnabled")}` : ""}
                                        </div>
                                    </div>
                                </td>

                                <td>
                                    <span style={ui.chip} title={t("stageList.labels.scheduleType")}>
                                        <i className="fas fa-layer-group" />
                                        {stage.scheduleType}
                                    </span>
                                </td>

                                <td className="text-right">
                                    <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                        {/* Schedule / results */}
                                        {/*stage.hasResults === false ? (*/}
                                            <NavLink
                                                to={`stage/${activeSeasonData.slug}`}
                                                state={{
                                                    activeSeasonData,
                                                    stageSlug: stage.slug,
                                                    stageId: stage.id,
                                                    division,
                                                    hasResults: stage.hasResults,
                                                    scheduleType: stage.scheduleType,
                                                    waivers,
                                                }}
                                                style={{ ...ui.actionBtn, ...ui.actionBtnEdit }}
                                                title={t("stageList.actions.openSchedule")}
                                            >
                                                <i className="fas fa-calendar-check" />
                                            </NavLink>
                                        {/*) : null*/}

                                        {/* Promote teams */}
                                        {stage.isDone === true &&
                                        stage.nextStage === true &&
                                        stage.scheduleType !== "TRAINING" ? (
                                            <button
                                                type="button"
                                                style={{ ...ui.actionBtn, ...ui.actionBtnSuccess }}
                                                onClick={() => teamsNextStage(stage.id)}
                                                data-toggle="modal"
                                                data-target="#teams-to-next-stage-modal"
                                                title={t("stageList.actions.advanceTeams")}
                                            >
                                                <i className="fas fa-arrow-alt-circle-right" />
                                            </button>
                                        ) : null}

                                        {/* Info when stage finished but next not created */}
                                        {stage.isDone === true && stage.nextStage === false ? (
                                            <span
                                                className="badge bg-danger"
                                                style={{ borderRadius: 999, padding: "8px 10px" }}
                                            >
                                                {t("stageList.badges.stageDoneCreateNext")}
                                            </span>
                                        ) : null}

                                        {/* Delete */}
                                        {canDelete ? (
                                            <button
                                                type="button"
                                                style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                                                onClick={() => requestRemoveStage(stage)}
                                                title={t("stageList.actions.deleteStage")}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        ) : null}
                                    </div>
                                </td>
                            </tr>
                        );
                    }}
                />

                {/* Scoped focus styles for actions inside this section (optional) */}
                <style>{`
          #kbola-stage-list button:focus,
          #kbola-stage-list a:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          }
        `}</style>
            </div>

            <AddStageModal
                stages={stages}
                setStages={setStages}
                activeSeasonData={activeSeasonData}
                modalElem={modalElem}
            />

            <TeamsToNextStageModal
                teamsToNextStage={teamsToNextStage}
                stageId={stageId}
                sportKey={division?.sport?.key}
            />
        </>
    );
}
